import _ from 'lodash'
import { Button } from 'primereact/button'
import React, { useEffect, useState } from 'react'
import {
  Address,
  GenericComboDTO,
  initialStateAddress,
  initialStatePhoneDTO,
  InputTable,
  mobilePhoneToString,
  Modal,
} from 'medere-ui-framework'
import HealthEntityActionMenu, { HealthEntityActionMenuItem } from './HealthEntityActionMenu'
import HealthEntityAddressForm from './HealthEntityAddressForm'

interface HealthEntityAddressInputProps {
  addresses: Address[]
  setAddresses: (addresses: Address[]) => void
  disabled?: boolean
  isMobile?: boolean
  readOnly?: boolean
  getAllAddressTypes: () => Promise<GenericComboDTO[]>
  modalDeletedConfirmed?: boolean
}

function normalizeEmails(value: string | null | undefined): string {
  return String(value ?? '')
    .split(';')
    .map((mail) => mail.trim())
    .filter((mail) => mail !== '')
    .join(';')
}

export default function HealthEntityAddressInput({
  addresses = [],
  setAddresses,
  disabled = false,
  isMobile = false,
  readOnly = false,
  getAllAddressTypes,
  modalDeletedConfirmed = true,
}: Readonly<HealthEntityAddressInputProps>) {
  const [newAddressTypes, setNewAddressTypes] = useState<GenericComboDTO[]>([])
  const [newAddress, setNewAddress] = useState<Address>(initialStateAddress)
  const [previousEditAddress, setPreviousEditAddress] = useState<Address>(initialStateAddress)
  const [modalViewAddressDeleted, setModalViewAddressDeleted] = useState<Address | null>(null)
  const [modalNewAddress, setModalNewAddress] = useState(false)

  useEffect(() => {
    getAllAddressTypes().then((res) => setNewAddressTypes(res ?? []))
  }, [getAllAddressTypes])

  function deleteAddressHandler(index: number) {
    modalDeletedConfirmed
      ? setModalViewAddressDeleted(addresses[index])
      : setAddresses(addresses.filter((_address, i) => i !== index))
  }

  function editAddressHandler(address: Address, index: number) {
    const _addresses = [...addresses]
    _addresses.splice(index, 1)
    setAddresses(_addresses)

    setNewAddress({
      ...address,
      phones: address.phones.length > 0 ? address.phones : [{ ...initialStatePhoneDTO }],
    })
    setPreviousEditAddress(address)
    setModalNewAddress(true)
  }

  function onAddAddress(createAnother?: boolean, addressToAdd?: Address) {
    const _newAddress = { ...(addressToAdd ?? newAddress) }
    _newAddress.phones = _newAddress.phones.filter((phone) => (phone.phonenumber?.number ?? '').trim() !== '')
    _newAddress.email = normalizeEmails(_newAddress.email)
    _newAddress.hasChanges = true

    setAddresses(addresses ? [...addresses, _newAddress] : [_newAddress])
    setNewAddress(initialStateAddress)

    if (!createAnother) {
      setModalNewAddress(false)
    }
  }

  function cancelChanges() {
    if (previousEditAddress.address > 0 && !_.isEqual(newAddress, initialStateAddress)) {
      setAddresses([...addresses, { ...previousEditAddress }])
    }
    setNewAddress(initialStateAddress)
    setModalNewAddress(false)
  }

  function formatAddressValue(value: string | null | undefined, emptyText = 'Sin datos') {
    const normalized = String(value ?? '').trim()
    return normalized !== '' ? normalized : emptyText
  }

  function resolveAddressTypes(address: Address) {
    const types = (address.types ?? [])
      .map((type) => String(type?.description ?? '').trim())
      .filter((type) => type !== '')
    return types.length > 0 ? types.join(', ') : 'Sin datos'
  }

  function resolveAddressPhones(address: Address) {
    const phones = (address.phones ?? [])
      .map((phone) => {
        const phoneNumber = mobilePhoneToString(phone.phonenumber).trim()
        if (phoneNumber === '') return ''
        const type = String(phone.typedesc ?? '').trim()
        return type !== '' ? `${type} ${phoneNumber}` : phoneNumber
      })
      .filter((phone) => phone !== '')
    return phones.length > 0 ? phones.join(' · ') : 'Sin datos'
  }

  function resolveAddressEmails(address: Address) {
    const emails = String(address.email ?? '')
      .split(';')
      .map((email) => email.trim())
      .filter((email) => email !== '')
    return emails.length > 0 ? emails.join(' ; ') : 'Sin datos'
  }

  return (
    <div className='flex flex-column'>
      <Modal
        header={<h1 className='info-dark text-2xl'>Eliminar domicilio</h1>}
        width='w-small'
        visible={!!modalViewAddressDeleted}
        setVisible={() => setModalViewAddressDeleted(null)}
        footerButtonLeftText='Cancelar'
        onClickLeftBtn={() => setModalViewAddressDeleted(null)}
        footerButtonRightText='Eliminar'
        onClickRightBtn={() => {
          setAddresses(addresses.filter((a) => a !== modalViewAddressDeleted))
          setModalViewAddressDeleted(null)
        }}
      />

      <Modal
        header={<h1 className='info-dark text-2xl'>{newAddress.address > 0 ? 'Editar' : 'Agregar'} domicilio</h1>}
        width='w-medium'
        onHideCustom={cancelChanges}
        visible={modalNewAddress}
        setVisible={() => setModalNewAddress(false)}
      >
        <HealthEntityAddressForm
          newAddress={newAddress}
          setNewAddress={setNewAddress}
          newAddressTypes={newAddressTypes}
          onConfirm={onAddAddress}
          onCancel={cancelChanges}
        />
      </Modal>

      <div className='flex justify-content-between align-items-baseline'>
        <div className='flex flex-row align-items-center mt-4'>
          <p className='font-medium text-lg health-entity-address-subtitle'>Domicilio</p>
        </div>
        {!readOnly && (
          <Button
            className='health-entity-add-address-btn'
            icon='pi pi-plus-circle'
            label='Agregar domicilio'
            onClick={() => {
              setPreviousEditAddress(initialStateAddress)
              setNewAddress(initialStateAddress)
              setModalNewAddress(true)
            }}
          />
        )}
      </div>

      {!disabled && isMobile && (
        <div className='health-entity-mobile-section-list'>
          {(addresses ?? []).map((address, index) => {
            const title = formatAddressValue(address.addressdesc, 'Sin dirección')
            const menuItems: HealthEntityActionMenuItem[] = readOnly
              ? []
              : [
                {
                  label: 'Editar',
                  icon: 'pi pi-file-edit',
                  action: () => editAddressHandler(address, index),
                },
                {
                  label: 'Eliminar',
                  icon: 'pi pi-trash',
                  action: () => deleteAddressHandler(index),
                },
              ]

            return (
              <article key={`health-entity-address-${address.address ?? index}-${index}`} className='health-entity-mobile-record-card'>
                <div className='health-entity-mobile-record-header'>
                  <h4 className='health-entity-mobile-record-title'>{title}</h4>
                  {!readOnly && (
                    <HealthEntityActionMenu
                      menuItems={menuItems}
                      buttonClassName='p-button-text health-entity-mobile-record-menu-btn'
                      buttonAriaLabel='Opciones del domicilio'
                    />
                  )}
                </div>

                <div className='health-entity-mobile-record-body'>
                  <div className='health-entity-mobile-record-row'>
                    <span className='health-entity-mobile-record-key'>Tipos</span>
                    <span className='health-entity-mobile-record-value'>{resolveAddressTypes(address)}</span>
                  </div>
                  <div className='health-entity-mobile-record-row'>
                    <span className='health-entity-mobile-record-key'>Ciudad</span>
                    <span className='health-entity-mobile-record-value'>{formatAddressValue(address.citydesc)}</span>
                  </div>
                  <div className='health-entity-mobile-record-row'>
                    <span className='health-entity-mobile-record-key'>Código postal</span>
                    <span className='health-entity-mobile-record-value'>{formatAddressValue(address.postalcode)}</span>
                  </div>
                  <div className='health-entity-mobile-record-row'>
                    <span className='health-entity-mobile-record-key'>Teléfonos</span>
                    <span className='health-entity-mobile-record-value'>{resolveAddressPhones(address)}</span>
                  </div>
                  <div className='health-entity-mobile-record-row'>
                    <span className='health-entity-mobile-record-key'>Emails</span>
                    <span className='health-entity-mobile-record-value'>{resolveAddressEmails(address)}</span>
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}

      {!disabled && !isMobile && (
        <InputTable
          items={addresses}
          freezeControlColumns
          onDelete={readOnly ? undefined : (_item, index) => deleteAddressHandler(index)}
          onEdit={readOnly ? undefined : editAddressHandler}
          onCancelEdit={readOnly ? undefined : cancelChanges}
          headerDivClassName='health-entity-address-table-header'
          className='overflow-auto'
          style={{ maxWidth: '100%', overflowX: 'auto' }}
          contentMinWidth={0}
          divBtnBodyClassName='bg-white'
          inputDivClassName='align-items-baseline'
          buttonColumnWidth={90}
          disabled={readOnly}
          itemsClassName='align-items-center my-1 mr-1 py-1'
          columnGap={3}
          iconAddBtn='pi pi-plus'
          columns={[
            {
              header: 'Tipo',
              className: 'w-full',
              style: { maxWidth: 170 },
              body: (item: Address) => item.types.map((t) => t.description).join(', '),
            },
            {
              header: 'Dirección',
              field: 'addressdesc',
              className: 'w-full',
            },
            {
              header: 'Ciudad',
              field: 'citydesc',
              className: 'w-full',
            },
            {
              header: 'Código Postal',
              field: 'postalcode',
              className: 'w-full',
              style: { maxWidth: 100 },
            },
            {
              header: 'Teléfono',
              body: (item: Address) => (
                <div className='flex flex-column'>
                  {item.phones.map((phone, i) => (
                    <div key={i} className='flex flex-row'>
                      <span>{`${phone.typedesc ? `${phone.typedesc} ` : ''}${mobilePhoneToString(phone.phonenumber)}`}</span>
                    </div>
                  ))}
                </div>
              ),
              className: 'w-full',
              style: { minWidth: 200 },
            },
            {
              header: 'Emails',
              field: 'email',
              className: 'hideText',
              style: { minWidth: 200, maxWidth: 200 },
            },
          ]}
        />
      )}
    </div>
  )
}
